/**
 * NextUpBar — the floating pill that drives a "Next up" work session
 * (docs/crm-work-queue-plan.md). Renders over every CRM page while a session
 * runs: current lead + why it's up, progress, Skip / Done / exit. An overlay
 * on purpose — it never touches ContactDetail's layout or height calcs; the
 * recruiter works in the real lead page and the bar just floats clear of it.
 *
 * Position: TOP-center, tucked under the sticky h-12 app bar at Z.overlay
 * (owner ask 2026-08-07 — at the bottom it sat on the composer and the lead
 * rail's peek, the surfaces you need while actually working the lead). The
 * list therefore opens DOWNWARD; the pill stays the fixed anchor. The lead
 * name is a link back to that lead, so a detour is one click from the work.
 *
 * Out-of-office coverage: while a teammate is out, their leads can appear in
 * this queue carrying a `coverage` ref. Two consequences are handled below.
 * (1) Every such row wears a <CoverageBadge> — silent coverage is how a lead
 * gets worked twice. (2) SNOOZE ON A COVERED LEAD DEFERS SOMEONE ELSE'S LEAD:
 * `snoozed_until` is a column on the contact, not a per-viewer preference, so
 * it hides the lead from the absent owner's Today list as well — and they are
 * not here to notice. The snooze menu therefore says so, by name, before the
 * click rather than after it.
 *
 * Outreach lists (docs/crm-outreach-lists-plan.md §B6): a lead on a list is
 * finished by LOGGING what happened, so whenever the row on deck carries a
 * `call_list` ref the outcome control takes the place of Snooze and "Done,
 * next" — a split button, narrower than the pair it replaces, so the pill
 * never grows past today's width. In a list session proper ("Working: <list
 * name>") the counter becomes the server's "42 left · 9 done today", skipped
 * rows fold behind "Show skipped", a poll can raise a "new reply — up next"
 * chip (it never moves the page), outside 8am–9pm a line asks for a
 * click-through before the next call, and the finished state says when the
 * next tries start instead of celebrating an empty list.
 *
 * Recruitment lists (docs/plans/crm-recruitment-list-plan.md B5): a study
 * session ("Working: <study> recruitment list", started by Work through on a
 * study's sheet) keeps Snooze, Skip and "Done, next", which move it on
 * exactly as they move a normal session, and hosts the sheet's attempt menu
 * where a list session hosts the outcome control (never both). A logged
 * attempt never moves the session on: a call often has two outcomes. "Back to
 * the list" returns to the sheet while the bar keeps the place; once the list
 * has run dry it ends the session there. Below md the pill wraps onto a
 * second row rather than run off a phone's screen.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, Check, ChevronUp, ClipboardCheck, ListChecks, Loader2, MessageSquare, MoonStar, PartyPopper, SkipForward, Sparkles, X, Zap,
} from 'lucide-react';
import { Z } from './ui';
import CoverageBadge, { returnDayLabel } from './CoverageBadge';
import { useWorkQueue } from '../WorkQueueContext';
import { useNow } from '../useNow';
import OutcomeControl from '../outreach/OutcomeControl';
import AttemptMenu from '../recruit/AttemptMenu';
import { studyItemTriesReason } from '../recruit/recruitLogic';
import { FLAG_LABELS } from '../outreach/outreachLogic';
import type { OutreachFlag } from '../outreach/types';
import { briefReasonLabel } from '../brief/briefLogic';
import type { Brief } from '../brief/types';
import {
  callingHoursBlock,
  callingHoursLine,
  eligibilityChip,
  listCounterLabel,
  listEmptyLine,
  listHref,
  newReplyLabel,
  parseSnoozeDays,
  preselectOutcome,
  reasonLabel,
  snoozeBlockedReason,
  snoozeReturnLabel,
  snoozeUntilDays,
  SNOOZE_PICKS,
  skippedStillDueLine,
  visibleRows,
  type QueueRowStatus,
} from '../workQueueLogic';

const actionBtn = 'inline-flex min-h-[44px] items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0';

const CHIP_TONE = {
  good: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  bad: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
} as const;

/** How the lead came in — the signal that decides where they sit in the queue. */
function EligibilityChip({ incoming, muted }: { incoming: string | null | undefined; muted?: boolean }) {
  const chip = eligibilityChip(incoming);
  if (!chip) return null;
  return (
    <span
      /* no-underline: worked/skipped rows strike their name through, and a
         struck-out chip reads as broken rather than as a done row. */
      className={`ml-1.5 inline-flex flex-shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-medium leading-none no-underline ${
        muted ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' : CHIP_TONE[chip.tone]
      }`}
      title={`Landing form: ${chip.label.toLowerCase()}`}
    >
      {chip.label}
    </span>
  );
}

/** Per-row leading marker: worked, skipped, on deck, or waiting. */
function RowMark({ status }: { status: QueueRowStatus }) {
  if (status === 'done') {
    return <Check className="h-3.5 w-3.5 flex-shrink-0 text-green-600 dark:text-green-500" aria-hidden="true" />;
  }
  if (status === 'skipped') {
    return <SkipForward className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-hidden="true" />;
  }
  if (status === 'current') {
    return <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-600 dark:bg-primary-400" aria-hidden="true" />;
  }
  return <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-300 dark:bg-gray-600" aria-hidden="true" />;
}

/**
 * The small "AI" mark beside a reason line the AI brief wrote (crm-brief plan
 * B6). Renders only when the item carries a brief with a why-line, which is
 * exactly when reasonLabel returns that why-line.
 */
function BriefMark({ brief }: { brief: Brief | null | undefined }) {
  if (!briefReasonLabel(brief)) return null;
  return (
    <span
      role="img"
      aria-label="Suggested by the AI brief"
      title="Suggested by the AI brief"
      data-brief-mark=""
      className="inline-flex flex-shrink-0 text-gray-400 dark:text-gray-500"
    >
      <Sparkles className="h-3 w-3" aria-hidden="true" />
    </span>
  );
}

const STATUS_WORD: Record<QueueRowStatus, string> = {
  done: 'done', skipped: 'skipped', current: 'on deck now', upcoming: 'waiting',
};

/**
 * The first of a list lead's preflight flags ("Was not eligible last time"),
 * muted, with the rest in the tooltip. Context for the call, not a status —
 * so it never takes the brand accent.
 */
function FlagChip({ flags }: { flags: OutreachFlag[] | undefined }) {
  const words = (flags || []).map((f) => FLAG_LABELS[f]).filter(Boolean);
  if (!words.length) return null;
  return (
    <span
      title={words.join(' · ')}
      className="inline-flex max-w-[10rem] flex-shrink items-center truncate rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-gray-600 no-underline dark:bg-gray-700 dark:text-gray-300"
    >
      <span className="truncate">{words[0]}</span>
      {words.length > 1 && <span className="ml-1 flex-shrink-0 text-gray-400">+{words.length - 1}</span>}
    </span>
  );
}

export default function NextUpBar() {
  const {
    active, finished, starting, item, position, total, rows, done_count, goTo, done, skip, exit,
    snoozeCurrent, snoozing, snoozeError, sessionTier, keepTryingCount, startKeepTrying,
    isListSession, listId, listName, listCounts, newReplies, freshReady, resumeList, resumeSkipped,
    outsideHoursAck, ackOutsideHours, advanceHeld,
    isStudySession, studyId, studyLabel, returnTo,
  } = useWorkQueue();
  const [listOpen, setListOpen] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);
  const [days, setDays] = useState('');
  const currentRowRef = useRef<HTMLButtonElement>(null);
  const snoozeRef = useRef<HTMLDivElement>(null);
  // Calling hours move on the quarter-minute, not per render; no timer while idle.
  const nowMs = useNow(15000, active);

  const blocked = item ? snoozeBlockedReason(item.reason.kind, item.call_list) : null;
  const customDays = parseSnoozeDays(days);
  // Present only while the lead on deck belongs to someone who is out today.
  const covering = item?.coverage || null;
  const coveredOwner = covering?.owner_name?.trim().split(/\s+/)[0] || 'a teammate';
  // A list lead is finished by logging an outcome — in a list session and in
  // a normal one alike — so the outcome control stands in for Snooze + Done.
  // A study session hosts the recruitment list's attempt menu instead, never
  // both (plan B5).
  const onList = (!isStudySession && item?.call_list) || null;
  const attemptStudyId = isStudySession ? studyId : null;
  const listTitle = listName || 'your outreach lists';
  // The list this session works through, when it works one ("Working: …").
  const workingTitle = isListSession ? listTitle : isStudySession ? studyLabel : null;
  const hoursBlock = callingHoursBlock(new Date(nowMs));
  // Outside 8am–9pm the next call needs a deliberate click-through. Shown
  // while an outcome control is up (that is when a call is about to happen)
  // or while an advance is being held for it.
  const showHoursLine = !!hoursBlock && !outsideHoursAck && (!!onList || advanceHeld) && !finished;
  const { rows: shownRows, hiddenSkipped } = visibleRows(rows, { listMode: isListSession, showSkipped });

  // Both dropdowns hang off the same pill, so only one is ever open.
  const toggleList = () => { setSnoozeOpen(false); setListOpen((o) => !o); };
  const toggleSnooze = () => { setListOpen(false); setSnoozeOpen((o) => !o); };
  // The attempt menu opens a dropdown of its own; pressing it closes the bar's.
  const closeBarMenus = () => { setListOpen(false); setSnoozeOpen(false); };

  const fireSnooze = (d: number) => {
    setSnoozeOpen(false);
    setDays('');
    snoozeCurrent(snoozeUntilDays(d));
  };

  // Advancing to another lead must not leave a stale menu hanging over it.
  useEffect(() => { setSnoozeOpen(false); setDays(''); }, [item?.contact_id]);

  // Outside click closes the snooze menu (the list is closed by its own toggle
  // or by picking a row, so it does not need this).
  useEffect(() => {
    if (!snoozeOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!snoozeRef.current?.contains(e.target as Node)) setSnoozeOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [snoozeOpen]);

  // Opening the list should land you on the row you are working, not at the
  // top of a long queue.
  useEffect(() => {
    if (listOpen) currentRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [listOpen, position]);

  // Escape closes whichever menu is open (bubble phase — ConfirmDialog owns
  // capture). Also fires while the days box has focus, which is where a
  // recruiter backing out is most likely to be.
  useEffect(() => {
    if (!listOpen && !snoozeOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setListOpen(false);
      setSnoozeOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [listOpen, snoozeOpen]);

  // The outcome control's confirmation line, hosted HERE rather than under
  // the control: it has to share one notice stack with the calling-hours line
  // and the new-reply chip, which appear at the same moment (an outcome
  // logged after 9pm raises both), and two floating boxes would overlap.
  const [flash, setFlash] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => {
    if (!flash) return undefined;
    const t = window.setTimeout(() => setFlash(null), flash.tone === 'error' ? 10_000 : 6_000);
    return () => window.clearTimeout(t);
  }, [flash]);
  // Ending the session takes its confirmation line with it.
  useEffect(() => { if (!active) setFlash(null); }, [active]);

  if (!active) return null;

  const counterFull = isListSession
    ? listCounterLabel(listCounts?.remaining, listCounts?.done_today)
    : `${position} of ${total}`;
  // Phones get the short half ("42 left"); the done-today half is a nicety.
  const counterShort = isListSession ? listCounterLabel(listCounts?.remaining, null) : counterFull;
  const skippedTotal = rows.filter((r) => r.status === 'skipped').length;
  // A list that ran out only because the recruiter skipped people who are
  // still due must say so — and offer the way back, since the list view (and
  // its "Show skipped") is not shown in the finished state.
  const skippedDue = isListSession ? skippedStillDueLine(listCounts, skippedTotal) : null;
  // One filled button in the finished state: what the recruiter most likely
  // wants next. People the poll found beat people they chose to skip.
  const finishedLead: 'fresh' | 'skipped' | 'back' = freshReady > 0 ? 'fresh' : skippedDue ? 'skipped' : 'back';
  const quietBtn = `${actionBtn} border border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700`;
  const filledBtn = `${actionBtn} bg-primary-600 text-white hover:bg-primary-700`;
  // The finished state of an urgent (`now`) session offers the keep-trying leads when there are any.
  const keepTryingOffer = sessionTier === 'now' && typeof keepTryingCount === 'number' && keepTryingCount > 0;
  // A study session's pill carries the attempt menu and "Back to the list" as
  // well as Snooze, Skip and Done. Below md it wraps (who and why on the first
  // row, the controls under them, right-aligned) instead of running off the
  // screen; from md up it is the one-row pill every session has.
  const pillShape = finished && (isListSession || isStudySession)
    ? 'rounded-2xl'
    : isStudySession
      ? 'flex-wrap justify-end gap-y-1 rounded-2xl md:flex-nowrap md:justify-start md:rounded-full'
      : 'rounded-full';

  return (
    <div
      ref={snoozeRef}
      role="region"
      aria-label={workingTitle ? `Working: ${workingTitle}` : 'Next up work session'}
      /* top-14 = the sticky app bar (h-12) plus a 0.5rem gap, on every route
         and breakpoint — the app bar is the same height everywhere. The
         translate also makes this box the containing block of a fixed child:
         below md the attempt menu hangs from it (recruit/AttemptMenu). */
      className={`fixed left-1/2 top-14 ${Z.overlay} w-max max-w-[calc(100vw-1.5rem)] -translate-x-1/2`}
    >
      <div
        className={`flex items-center gap-2 border border-gray-200 bg-white py-1.5 pl-3 pr-1.5 shadow-lg dark:border-gray-700 dark:bg-gray-800 ${pillShape}`}
      >
        {finished && isListSession ? (
          <>
            {/* Nobody to call RIGHT NOW is not "all clear": the list still has
                people waiting on a next try, a text, or a booked call — so it
                says when, keeps polling, and offers the way back. A list that
                ran out because due people were skipped says THAT instead, and
                offers them back. */}
            <ListChecks className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            <span className="min-w-0 max-w-[20rem] text-sm font-medium leading-snug text-gray-900 dark:text-gray-100">
              {skippedDue ?? listEmptyLine(listCounts)}
            </span>
            {freshReady > 0 && (
              <button
                type="button"
                onClick={resumeList}
                className={filledBtn}
              >
                Keep going · {freshReady} ready
              </button>
            )}
            {skippedDue && (
              <button
                type="button"
                onClick={resumeSkipped}
                title="Bring the people you skipped back into this sitting"
                className={finishedLead === 'skipped' ? filledBtn : quietBtn}
              >
                Go back to them
              </button>
            )}
            <button
              type="button"
              onClick={() => exit(listHref(listId))}
              className={finishedLead === 'back' ? filledBtn : quietBtn}
            >
              Back to the list
            </button>
            <button
              type="button"
              onClick={() => exit()}
              aria-label="Stop working this list"
              className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-300 sm:h-7 sm:w-7"
            >
              <X className="h-4 w-4" />
            </button>
          </>
        ) : finished && isStudySession ? (
          <>
            {/* The rows ran out and the refill found nobody new. Rows still
                waiting on a next try, a snooze or a booked call are on the
                sheet, which is where this sends the recruiter back to. When
                the urgent rows are done and keep-trying ones are left, the
                bar offers those (owner 2026-09-28: urgent first, then offer
                the rest). */}
            <ClipboardCheck className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            <span className="min-w-0 max-w-[20rem] text-sm font-medium leading-snug text-gray-900 dark:text-gray-100">
              {keepTryingOffer
                ? 'Nothing on this list needs action now.'
                : sessionTier === 'later'
                  ? 'Nothing left to keep trying on this list right now.'
                  : 'Nothing left to work on this list right now.'}
            </span>
            {keepTryingOffer && (
              <button
                type="button"
                onClick={() => { void startKeepTrying(); }}
                disabled={starting}
                title="Work the leads due another try who are not waiting on us"
                className={`${filledBtn} disabled:cursor-wait disabled:opacity-60`}
              >
                Keep trying · {keepTryingCount}
              </button>
            )}
            <button
              type="button"
              onClick={() => exit(returnTo ?? true)}
              className={keepTryingOffer ? quietBtn : filledBtn}
            >
              Back to the list
            </button>
            <button
              type="button"
              onClick={() => exit()}
              aria-label="Stop working this list"
              className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-300 sm:h-7 sm:w-7"
            >
              <X className="h-4 w-4" />
            </button>
          </>
        ) : finished ? (
          <>
            <PartyPopper className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {sessionTier === 'later' ? 'Nothing left to keep trying right now' : 'All clear — nothing needs action now'}
            </span>
            {/* Urgent first, then offer the rest (owner 2026-09-28): the
                keep-trying leads are a click away, never started unasked. */}
            {keepTryingOffer && (
              <button
                type="button"
                onClick={() => { void startKeepTrying(); }}
                disabled={starting}
                title="Work the leads due another try who are not waiting on us"
                className={`${filledBtn} disabled:cursor-wait disabled:opacity-60`}
              >
                Keep trying · {keepTryingCount}
              </button>
            )}
            <button
              type="button"
              onClick={() => exit(true)}
              className={keepTryingOffer ? quietBtn : filledBtn}
            >
              Back to Today
            </button>
          </>
        ) : (
          <>
            <Zap className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            <div className={isStudySession ? 'min-w-0 flex-1 md:max-w-xs md:flex-initial' : 'min-w-0 max-w-[16rem] sm:max-w-xs'}>
              {/* The name is the way back to the lead: the bar floats over
                  every CRM page, so once you detour (Today, the calendar, a
                  search) one click returns you to the lead you are working. */}
              <div className="truncate text-sm font-medium leading-tight text-gray-900 dark:text-gray-100">
                {item?.href ? (
                  <Link
                    to={item.href}
                    title={`Open ${item.name}`}
                    className="rounded hover:text-primary-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:text-primary-300"
                  >
                    {item.name}
                  </Link>
                ) : item?.name}
                {/* In a list session the study IS the list's study, so its slot
                    says which list you are working instead; a study session
                    names its recruitment list. */}
                {workingTitle ? (
                  <span className="ml-1.5 text-xs font-normal text-gray-400 dark:text-gray-500">Working: {workingTitle}</span>
                ) : item?.study_title && (
                  <span className="ml-1.5 text-xs font-normal text-gray-400 dark:text-gray-500">{item.study_title}</span>
                )}
                <EligibilityChip incoming={item?.incoming_eligibility} />
              </div>
              <div className="flex min-w-0 items-center gap-1.5">
                <BriefMark brief={item?.brief} />
                <span className="truncate text-xs leading-tight text-gray-500 dark:text-gray-400">
                  {item ? reasonLabel(item.reason, undefined, item.call_list, item.brief) : ''}
                </span>
                {onList && <FlagChip flags={onList.flags} />}
                {/* Whose lead this really is — the recruiter must never mistake
                    a covered row for one reassigned to them. */}
                <CoverageBadge coverage={covering} compact />
              </div>
            </div>
            {/* The progress counter IS the list toggle — the affordance the
                bar was missing: "3 of 17" is the obvious place to look for
                the other 16. In a list session it is the server's count of
                who is left, not a position in a snapshot. */}
            <button
              type="button"
              onClick={toggleList}
              aria-expanded={listOpen}
              aria-label={listOpen ? 'Hide the list' : `Show the list — ${counterFull}`}
              title={listOpen ? 'Hide the list' : 'Show the whole list'}
              className="inline-flex min-h-[44px] flex-shrink-0 items-center gap-1 rounded-full border border-gray-300 px-2.5 text-xs font-medium tabular-nums text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0 sm:py-1.5"
            >
              {listOpen
                ? <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                : <ListChecks className="h-3.5 w-3.5" aria-hidden="true" />}
              <span className="sm:hidden">{counterShort}</span>
              <span className="hidden sm:inline">{counterFull}</span>
            </button>
            {/* Below md a study session's controls start a row of their own,
                right-aligned; the attempt menu then hangs from the whole bar
                rather than from its control, the first on that row. */}
            {isStudySession && <span aria-hidden="true" className="h-0 basis-full md:hidden" />}
            {attemptStudyId && item && (
              /* A study session: what happened on the call, through the
                 sheet's own attempt menu (plan B5). It never moves the session
                 on (a call often has two outcomes: "Talked", then a status
                 change on the lead page), so Snooze, Skip and Done stay beside
                 it. Its confirmation line joins the notice stack below. The
                 tries are off for a lead the sheet would switch them off for
                 (archived, or not open on the study), by the sheet's own rule. */
              <div className="contents" onClickCapture={closeBarMenus}>
                <AttemptMenu
                  studyId={attemptStudyId}
                  contactId={item.contact_id}
                  surface="bar"
                  name={item.name}
                  triesDisabledReason={studyItemTriesReason(item)}
                  onFlash={(tone, text) => setFlash({ tone, text })}
                />
              </div>
            )}
            {onList && item ? (
              /* A list lead: log what happened. Replaces Snooze + "Done,
                 next" — a list lead's "see you later" is Try later on the
                 list, and "done" means an outcome, never a bare click. */
              <OutcomeControl
                listId={onList.list_id}
                memberId={onList.member_id}
                contactId={item.contact_id}
                surface="bar"
                preselect={preselectOutcome(onList)}
                onFlash={(tone, text) => setFlash({ tone, text })}
              />
            ) : (
              /* Snooze — the durable "nothing needed here". Done only clears
                 the lead for THIS sitting; this one keeps them off the queue
                 until the day you pick. Label collapses to the icon on phones,
                 where the pill is already tight. */
              <button
                type="button"
                onClick={toggleSnooze}
                disabled={!!blocked || snoozing}
                aria-expanded={snoozeOpen}
                aria-haspopup="true"
                title={
                  blocked
                  || (covering
                    ? `Nothing needed now — put this lead out for a while. This is ${coveredOwner}'s lead: snoozing hides it from them too.`
                    : 'Nothing needed now — put this lead out for a while')
                }
                className={`${actionBtn} border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700`}
              >
                {snoozing
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  : <MoonStar className="h-3.5 w-3.5" aria-hidden="true" />}
                <span className="hidden sm:inline">Snooze</span>
                <span className="sr-only sm:hidden">Snooze this lead</span>
              </button>
            )}
            <button
              type="button"
              onClick={skip}
              title={onList ? 'Not now — skip them for this sitting (nothing is logged)' : undefined}
              className={`${actionBtn} border border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700`}
            >
              <SkipForward className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">Skip</span>
              <span className="sr-only sm:hidden">Skip this lead</span>
            </button>
            {!onList && (
              <button
                type="button"
                onClick={done}
                className={`${actionBtn} bg-primary-600 text-white hover:bg-primary-700`}
              >
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                Done, next
              </button>
            )}
            {isStudySession && returnTo && (
              /* Back to the sheet the session started from. A detour, not an
                 exit: the bar floats over the sheet too and keeps the place. */
              <Link to={returnTo} title="Back to the list" className={quietBtn}>
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="hidden lg:inline">Back to the list</span>
                <span className="sr-only lg:hidden">Back to the list</span>
              </Link>
            )}
            <button
              type="button"
              onClick={() => exit()}
              aria-label={workingTitle ? 'Stop working this list' : 'Exit work session'}
              className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-300 sm:h-7 sm:w-7"
            >
              <X className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {/* One stack of notices under the pill, right-aligned under the
          controls that raised them. */}
      {(flash || showHoursLine || (isListSession && !finished && newReplies > 0) || snoozeError) && (
        <div className="mt-2 flex flex-col items-end gap-1.5">
          {flash && (
            <div
              role={flash.tone === 'error' ? 'alert' : 'status'}
              className={`flex w-max max-w-full items-start gap-1.5 rounded-lg border px-2.5 py-1 text-xs leading-snug shadow-sm ${
                flash.tone === 'error'
                  ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300'
                  : 'border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
              }`}
            >
              {flash.tone === 'ok' && <Check className="mt-px h-3.5 w-3.5 flex-shrink-0 text-green-600 dark:text-green-500" aria-hidden="true" />}
              <span>{flash.text}</span>
            </div>
          )}

          {/* Outside 8am–9pm: the next call needs a reason. The bar still
              shows who is next; the page waits for this click, and outcomes
              logged after it are stamped as outside calling hours. */}
          {showHoursLine && hoursBlock && (
            <div
              role="alert"
              className="flex w-max max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-800 shadow-sm dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
            >
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              <span>{callingHoursLine(hoursBlock)}</span>
              <button
                type="button"
                onClick={ackOutsideHours}
                title="They asked for this time, or there is another good reason. Outcomes you log will note it was outside calling hours."
                className="inline-flex min-h-[44px] items-center rounded-md border border-amber-300 bg-white px-2 py-0.5 text-xs font-medium text-amber-800 transition-colors duration-150 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-amber-700 dark:bg-gray-800 dark:text-amber-300 dark:hover:bg-amber-900/30 sm:min-h-0"
              >
                {advanceHeld ? 'I have a reason — next person' : 'I have a reason'}
              </button>
            </div>
          )}

          {/* A reply came in on the list. Never moves the page — it will be
              the next person served after the outcome you log now. */}
          {isListSession && !finished && newReplies > 0 && (
            <div
              role="status"
              className="inline-flex w-max max-w-full items-center gap-1.5 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700 shadow-sm dark:bg-primary-900/40 dark:text-primary-300"
            >
              <MessageSquare className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              {newReplyLabel(newReplies)}
            </div>
          )}

          {snoozeError && (
            <div
              role="alert"
              className="w-max max-w-full rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
            >
              {snoozeError}
            </div>
          )}
        </div>
      )}

      {/* Snooze quick-picks. Hangs below the pill like the list, right-aligned
          under its trigger; the two are mutually exclusive so they never stack. */}
      {snoozeOpen && !finished && !onList && (
        <div className="ml-auto mt-2 w-56 rounded-xl border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {/* Snoozing writes crm.contacts.snoozed_until — one column on the
              lead, not a per-viewer preference. On a covered lead that defers
              it for the ABSENT owner too, who is not here to see it happen and
              would come back to a quietly buried lead. Say so before the click.
              Deliberately a warning, not a block: deferring a covered lead is
              often the right call, it just has to be a decision. */}
          {covering && (
            <div className="mb-1 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              <AlertTriangle className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              <span>
                This is {coveredOwner}&apos;s lead
                {returnDayLabel(covering.ends_on) ? ` (${returnDayLabel(covering.ends_on)})` : ''}.
                {' '}Snoozing hides it from their list too.
              </span>
            </div>
          )}
          <div className="px-2 pb-1 pt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
            Nothing needed now — bring them back
          </div>
          {SNOOZE_PICKS.map((p) => (
            <button
              key={p.days}
              type="button"
              onClick={() => fireSnooze(p.days)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-primary-50 focus:outline-none focus-visible:bg-primary-50 dark:text-gray-200 dark:hover:bg-primary-900/20 dark:focus-visible:bg-primary-900/20"
            >
              <MoonStar className="h-3.5 w-3.5 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              {p.label}
              <span className="ml-auto text-[11px] font-normal text-gray-400 dark:text-gray-500">
                {snoozeReturnLabel(snoozeUntilDays(p.days))}
              </span>
            </button>
          ))}
          <div className="mt-1 border-t border-gray-100 px-2 pb-1 pt-2 dark:border-gray-700">
            <label
              htmlFor="nextup-snooze-days"
              className="block text-[11px] font-medium text-gray-500 dark:text-gray-400"
            >
              Put it out
            </label>
            <div className="mt-1 flex items-center gap-1.5">
              <input
                id="nextup-snooze-days"
                type="number"
                min={1}
                max={365}
                value={days}
                placeholder="14"
                onChange={(e) => setDays(e.target.value)}
                onKeyDown={(e) => {
                  // Enter is the obvious commit here; without this it would
                  // submit nothing and the menu would just sit there.
                  if (e.key === 'Enter' && customDays) {
                    e.preventDefault();
                    fireSnooze(customDays);
                  }
                }}
                className="w-14 rounded border border-gray-300 px-1.5 py-1 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">days</span>
              <button
                type="button"
                onClick={() => customDays && fireSnooze(customDays)}
                disabled={!customDays}
                className="ml-auto rounded-md bg-primary-600 px-2 py-1 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                Snooze
              </button>
            </div>
            <p className="mt-1 min-h-[14px] text-[11px] text-gray-400 dark:text-gray-500">
              {customDays ? `Back on ${snoozeReturnLabel(snoozeUntilDays(customDays))}` : ''}
            </p>
          </div>
        </div>
      )}

      {/* The queue as an actual list: what you have worked, what is on deck,
          and what is still waiting — with any row clickable to jump there.
          Hangs BELOW the pill, which stays the fixed anchor under the app bar. */}
      {listOpen && !finished && (
        <div className="mt-2 max-h-[min(60vh,22rem)] w-[min(24rem,calc(100vw-1.5rem))] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <div className="sticky top-0 flex items-center justify-between gap-2 border-b border-gray-100 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
            {workingTitle ? (
              <span className="min-w-0 truncate text-xs font-semibold text-gray-700 dark:text-gray-200">
                Working: {workingTitle}
              </span>
            ) : (
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                Your list
              </span>
            )}
            <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
              {isListSession ? counterFull : `${done_count} of ${total} done`}
            </span>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {shownRows.map((row) => (
              <li key={row.item.key}>
                <button
                  type="button"
                  ref={row.status === 'current' ? currentRowRef : undefined}
                  onClick={() => { goTo(row.index); setListOpen(false); }}
                  aria-current={row.status === 'current' ? 'true' : undefined}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20 ${
                    row.status === 'current' ? 'bg-primary-50/60 dark:bg-primary-900/20' : ''
                  }`}
                >
                  <RowMark status={row.status} />
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block truncate text-sm ${
                        row.status === 'done' || row.status === 'skipped'
                          ? 'text-gray-400 line-through dark:text-gray-500'
                          : 'font-medium text-gray-900 dark:text-gray-100'
                      }`}
                    >
                      {row.item.name}
                      <EligibilityChip
                        incoming={row.item.incoming_eligibility}
                        muted={row.status === 'done' || row.status === 'skipped'}
                      />
                    </span>
                    <span className="flex min-w-0 items-center gap-1.5">
                      <BriefMark brief={row.item.brief} />
                      <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {reasonLabel(row.item.reason, undefined, row.item.call_list, row.item.brief)}
                      </span>
                      <CoverageBadge coverage={row.item.coverage} compact hideReturn />
                    </span>
                  </span>
                  <span className="sr-only">{STATUS_WORD[row.status]}</span>
                </button>
              </li>
            ))}
            {/* Skipped rows fold away in a list session — the list is long
                and a skip means "not now" — but they stay one click away. */}
            {isListSession && skippedTotal > 0 && (
              <li>
                <button
                  type="button"
                  onClick={() => setShowSkipped((v) => !v)}
                  aria-expanded={showSkipped}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-gray-500 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus-visible:bg-primary-50 dark:text-gray-400 dark:hover:bg-gray-700/50 dark:hover:text-gray-200"
                >
                  <SkipForward className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                  {showSkipped ? 'Hide skipped' : `Show skipped (${hiddenSkipped})`}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
